import React, { useCallback, useMemo, useState } from 'react';
import { Part } from '~/types';
import classes from './style.module.scss';
import { useStoreActions, useStoreState } from '~/store';
import Button from '~/app/common/Button';
import Quantity from '~/app/common/Quantity';
import Icon from '~/app/common/Icon';
import useTranslation from '~/app/hooks/useTranslation';
import InfiniteScroll from 'react-infinite-scroll-component';
import { PartLinkTypes, PartTypes } from '~/app/data/statuses/issue';
import useAccessControl from '~/app/hooks/useAccessControl';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { toast } from 'react-toastify';
import PartRowMenu from '../../PartRowMenu';
import { formatPrice } from '~/app/helpers/format';
import { getPartStoreKey, isPartInTemp } from '../../helper';

export interface SelectedPartListProps {
  searchText: string
  editManualPart: (part: Part) => void
  type: PartLinkTypes
}

export type SelectedPartListComponent = React.FC<SelectedPartListProps>

const SelectedPartList: SelectedPartListComponent = ({ searchText, editManualPart, type }) => {

  const basePath = getPartStoreKey(type);
  const deptPrefix = useStoreState(state => state[basePath].data.departmentPrefix);
  const tempParts = useStoreState(state => state[basePath].tempParts ?? []);
  const searchParts = useStoreState(state => state[basePath].searchParts ?? []);
  const searchPartsTotal = useStoreState(state => state[basePath].searchPartsTotal ?? 0);
  const searchPartAction = useStoreActions(actions => actions[basePath].fetchSearchParts ?? []);
  const setTempPartsAction = useStoreActions(actions => actions[basePath].setTempParts);

  const { t } = useTranslation('parts');
  const { checkPermission } = useAccessControl();

  const partsOutOfStockPermission = deptPrefix
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.partsOutOfStock(deptPrefix))
    : false;

  const selectedParts = useMemo(
    () => tempParts.filter(m => !m.deleted),
    [tempParts]
  );

  const inventoryParts = useMemo(
    () => searchParts.filter(m => !isPartInTemp(m, tempParts)),
    [searchParts, tempParts]
  );

  const [selectedExpanded, setSelectedExpanded] = useState(true);
  const [inventoryExpanded, setInventoryExpanded] = useState(true);

  const getAllowUpdate = useCallback((part: Part, quantity: number) => {
    if (!partsOutOfStockPermission && part.type === PartTypes.AUTO) {
      const inStock = (part.in_stock - quantity) >= 0;
      if (!inStock) {
        toast.error(t('outOfStock'));
      }
      return inStock;
    }
    return true;
  }, [partsOutOfStockPermission, t]);

  const handleAddPart = useCallback((part: Part) => {
    const alreadyExists = tempParts.some(m => m.id.toString().trim() === part.id.toString().trim() && !m.deleted);
    if (!alreadyExists) {
      const updatedParts = [...tempParts];
      updatedParts.push({ ...part, quantity: 1, added: true });
      setTempPartsAction(updatedParts);
    }
  }, [tempParts, setTempPartsAction]);

  const handleQuantityChange = useCallback((id: string, value: number) => {
    const updatedParts = [...tempParts];
    const index = updatedParts.findIndex(tm => tm.id === id);

    if (index !== -1) {
      const existingPart = updatedParts[index];
      if (getAllowUpdate(existingPart, value)) {
        updatedParts[index] = { ...existingPart, quantity: value, deleted: false };
      }
    } else {
      const partFromSearch = searchParts.find(m => m.id === id);
      if (partFromSearch && getAllowUpdate(partFromSearch, value)) {
        updatedParts.push({ ...partFromSearch, quantity: value, added: true });
      }
    }

    setTempPartsAction(updatedParts);
  }, [tempParts, searchParts, setTempPartsAction, getAllowUpdate]);

  const handleDelete = useCallback((part: Part) => {
    const updatedParts = [...tempParts];
    const index = updatedParts.findIndex(tm => tm.id === part.id);
    if (index !== -1) {
      updatedParts[index] = {
        ...updatedParts[index],
        deleted: true,
      };
      setTempPartsAction(updatedParts);
    }
  }, [tempParts, setTempPartsAction]);

  const fetchNext = useCallback(() => {
    searchPartAction({ searchText, offset: searchParts.length });
  }, [searchPartAction, searchText, searchParts.length]);

  return (
    <div className={classes['partsList']} id='search-scroll-wrapper'>
      <div className={classes['section']}>
        <button
          type="button"
          className={classes['sectionHeader']}
          onClick={() => setSelectedExpanded(prev => !prev)}
        >
          <span>{t('selected')} ({selectedParts.length})</span>
          <Icon name={selectedExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} />
        </button>
        {selectedExpanded && (
          selectedParts.length === 0 ? (
            <p className={classes['emptySection']}>{t('noPartsFound')}</p>
          ) : (
            <div className={classes['sectionBody']}>
              {selectedParts.map((part) => (
                <div className={`${classes['partItem']} ${classes['selectedItem']}`} key={part.id}>
                  <div className={classes['column']}>
                    <span className={classes['partName']}>{part.article_name}</span>
                    <span className={classes['partNumber']}>{t('partNo')}: {part.article_no} · {formatPrice(part.price_per_unit)}</span>
                  </div>
                  <div className={classes['actions']}>
                    <Quantity
                      quantity={part.quantity}
                      quantityChange={(value) => handleQuantityChange(part.id, value)}
                    />
                    <PartRowMenu
                      part={part}
                      onEdit={part.type === PartTypes.MANUAL ? editManualPart : undefined}
                      onRemove={handleDelete}
                    />
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <div className={classes['section']}>
        <button
          type="button"
          className={classes['sectionHeader']}
          onClick={() => setInventoryExpanded(prev => !prev)}
        >
          <span>{t('inventory')} ({inventoryParts.length})</span>
          <Icon name={inventoryExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} />
        </button>
        {inventoryExpanded && (
          inventoryParts.length === 0 ? (
            <p className={classes['emptySection']}>{t('noPartsFound')}</p>
          ) : (
            <InfiniteScroll
              dataLength={searchParts.length}
              next={fetchNext}
              hasMore={searchParts.length < searchPartsTotal}
              loader={<div></div>}
              scrollableTarget="search-scroll-wrapper"
            >
              {inventoryParts.map((part) => (
                <div className={classes['partItem']} key={part.id}>
                  <div className={classes['row']}>
                    <div className={classes['column']}>
                      <span className={classes['partName']}>{part.article_name}</span>
                      <span className={classes['partNumber']}>{t('partNo')}: {part.article_no} · {formatPrice(part.price_per_unit)}</span>
                    </div>
                  </div>
                  <div className={classes['column']}>
                    {partsOutOfStockPermission === false && part.in_stock < 1 ? (
                      <p className={classes['outOfStock']}>{t('outOfStock')}</p>
                    ) : (
                      <Button className={classes['addButton']} onClick={() => handleAddPart(part)}>
                        {t('add')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </InfiniteScroll>
          )
        )}
      </div>
    </div>
  );
};

export default SelectedPartList;
